import { useEffect, useMemo, useState, memo } from "react";
import { BrowserRouter as Router, useNavigate, useLocation, Navigate } from "react-router-dom";
import { ChakraProvider, extendTheme, Box, Button, Code, Flex, Heading, Text } from "@chakra-ui/react";
import { Amplify, Hub } from "aws-amplify";
import { Toaster } from "react-hot-toast";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { ThemeProvider } from "styled-components";
import AppRoutes from "./AppRoutes";
import { GlobalStyles } from "./Assets/Styles/GlobalStyles";
import awsconfig from "./aws-exports";
import ErrorBoundary from "./Reusable/ErrorBoundary/ErrorBoundary";
import { vars } from "./Theme.css";
import AuthRoutes from "./AuthRoutes";
import { CookiesProvider } from "react-cookie";
import AppAccessForm from "./Components/AppAccessForm";
import {  useSelector } from "react-redux";
import useSignout from "./hooks/useSignout";
import { useLazyGetProfileQuery } from "../redux/reducers/apiSlice";
import { CookieConsent } from './Components/CookieConsent';
import FullPageNexusLogoAnimation from "./Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";

// Move AppContent outside App to prevent it from being recreated on every render
// This is CRITICAL - if AppContent is defined inside App, it gets a new function reference
// on every render, causing AppRoutes and TalkJSSession to remount
const AppContent = memo(({ token, isCheckingAuth, isAuthenticated, profileDataError, profileFetchTimeout }: { 
  token: string | null;
  isCheckingAuth: boolean; 
  isAuthenticated: boolean; 
  profileDataError: any; 
  profileFetchTimeout: boolean;
}) => {
  const location = useLocation();
  
  const authRoutes = ["/login", "/signup", "/confirm-signup", "/forgot-password", "/reset-password"];
  const isOnAuthRoute = authRoutes.includes(location.pathname);
  
  // Show loader while checking auth
  if (isCheckingAuth) {
    return <FullPageNexusLogoAnimation />;
  }
  
  // If profile fetch failed or timed out, show error but allow navigation
  // This prevents getting stuck in loading state
  if (token && (profileDataError || profileFetchTimeout)) {
    console.warn('Profile fetch failed or timed out, but token exists. Allowing navigation.');
    // If we have a token but profile fetch failed, still try to show app
    // The AppRoutes will handle the error and sign out if needed
    if (isOnAuthRoute) {
      return <Navigate to="/" replace />;
    }
    return (
      <>
        <AppRoutes />
        <CookieConsent />
      </>
    );
  }
  
  // CRITICAL: If authenticated but on auth route, redirect immediately using Navigate component
  // This is synchronous and prevents AppRoutes from rendering on invalid route (404 blink)
  if (isAuthenticated && isOnAuthRoute) {
    return <Navigate to="/" replace />;
  }
  
  if (isAuthenticated) {
    return (
      <>
        <AppRoutes />
        <CookieConsent />
      </>
    );
  }
  
  return <AuthRoutes />;
});

function getMissingCognitoEnvKeys() {
  const required = ["VITE_COGNITO_USER_POOL_ID", "VITE_COGNITO_USER_POOL_WEB_ID"] as const;
  return required.filter((k) => {
    // Vite exposes env on import.meta.env
    // @ts-ignore
    const val = import.meta.env?.[k];
    return !val || String(val).trim() === "" || String(val).toLowerCase() === "undefined";
  });
}

function MissingCognitoConfigScreen({ missingKeys }: { missingKeys: string[] }) {
  return (
    <Flex minH="100vh" align="center" justify="center" px={6} bg="white">
      <Box maxW="720px" w="full" borderWidth="1px" borderRadius="lg" p={6}>
        <Heading size="md" mb={3}>
          Local environment config missing
        </Heading>
        <Text mb={4}>
          App is crashing because AWS Cognito (Amplify) env variables are not set. Please add the following keys to{" "}
          <Code>.env.local</Code> in the project root and restart <Code>npm run dev</Code>.
        </Text>

        <Box mb={4}>
          <Text fontWeight="bold" mb={2}>
            Missing keys
          </Text>
          <Box as="ul" pl={5}>
            {missingKeys.map((k) => (
              <Box as="li" key={k}>
                <Code>{k}</Code>
              </Box>
            ))}
          </Box>
        </Box>

        <Text fontWeight="bold" mb={2}>
          Example
        </Text>
        <Box
          as="pre"
          p={3}
          borderRadius="md"
          bg="gray.50"
          borderWidth="1px"
          overflowX="auto"
          fontSize="sm"
          mb={4}
        >{`VITE_COGNITO_USER_POOL_ID=us-east-1_XXXXXXXXX
VITE_COGNITO_USER_POOL_WEB_ID=xxxxxxxxxxxxxxxxxxxxxxxxxx`}</Box>

        <Button onClick={() => window.location.reload()} colorScheme="orange">
          Reload after setting env
        </Button>
      </Box>
    </Flex>
  );
}

function App() {
  const missingCognitoKeys = useMemo(() => getMissingCognitoEnvKeys(), []);

  useEffect(() => {
    if (missingCognitoKeys.length === 0) {
      try {
        Amplify.configure(awsconfig);
      } catch (e) {
        console.error("Amplify.configure failed:", e);
      }
    }
  }, [missingCognitoKeys]);

  const token = useSelector((state: { auth: { token: string; } }) => state.auth.token)
  const { signOut } = useSignout();
  const [getProfile, { data: profileData, isLoading: profileDataIsLoading, error: profileDataError, isFetching: profileDataIsFetching }] = useLazyGetProfileQuery();
  const [hasTriggeredProfileFetch, setHasTriggeredProfileFetch] = useState(false);
  const [profileFetchTimeout, setProfileFetchTimeout] = useState(false);
  const isAuthenticated = !!token && profileData?.id;
  
  // If we have a token but profile is still loading/fetching or hasn't been fetched yet, show loading
  // This prevents showing AuthRoutes (HomePage) while checking authentication
  // Key: If token exists but no profile data (and not an error), we're still checking
  // BUT: If there's an error or timeout, stop checking and show error
  const isCheckingAuth = !!token && !isAuthenticated && !profileDataError && !profileFetchTimeout && (
    !hasTriggeredProfileFetch || // Haven't started fetching yet
    profileDataIsLoading || 
    profileDataIsFetching || 
    (hasTriggeredProfileFetch && !profileData && !profileDataError) // Fetching but no data yet
  );

  const chakraTheme = extendTheme({
    fonts: { ...vars.fonts },
    colors: {
      primary: {
        50: "#f9c7b7",
        100: "#f7b19a",
        200: "#f59c7e",
        300: "#f38662",
        400: "#f07045",
        500: vars.color.primary,
        600: "#e64712",
        700: "#ca3e10",
        800: "#ad350e",
        900: "#912d0c",
      },
    },
    sizes: { ...vars.sizes },
    breakpoints: { ...vars.breakpoints },
    styles: {
      global: {
        a: {
          color: vars.color.primary,
        },
      },
    },
  });

  useEffect(() => {
    if(token && !hasTriggeredProfileFetch){
      setHasTriggeredProfileFetch(true);
      setProfileFetchTimeout(false);
      
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.error('Profile fetch timeout - backend may be slow or unreachable');
        setProfileFetchTimeout(true);
      }, 30000); // 30 second timeout
      
      getProfile().finally(() => {
        clearTimeout(timeoutId);
      });
    } else if(!token) {
      setHasTriggeredProfileFetch(false);
      setProfileFetchTimeout(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[token])
  
  // Log errors for debugging
  useEffect(() => {
    if(profileDataError) {
      console.error('Profile fetch error:', profileDataError);
    }
  }, [profileDataError])

  const handleUserEvent = async (data) => {
    if (data?.payload?.event === "tokenRefresh_failure") {
      signOut();
    }
  };

  useEffect(() => {
    Hub.listen("auth", handleUserEvent);
  }, []);


  if (
    window.localStorage.getItem("app_access") !== "true" &&
    // @ts-ignore
    import.meta.env.MODE === "staging"
  ) {
    return <AppAccessForm />;
  }

  if (missingCognitoKeys.length > 0) {
    return <MissingCognitoConfigScreen missingKeys={missingCognitoKeys} />;
  }

  return (
    <ChakraProvider theme={chakraTheme}>
      <CookiesProvider>
        {/* @ts-ignore */}
        <ThemeProvider theme={vars}>
          {/* @ts-ignore */}
          <GlobalStyles />
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ErrorBoundary>
              <AppContent 
                token={token}
                isCheckingAuth={isCheckingAuth}
                isAuthenticated={isAuthenticated}
                profileDataError={profileDataError}
                profileFetchTimeout={profileFetchTimeout}
              />
            </ErrorBoundary>
          </Router>
        </ThemeProvider>
        <Toaster />
      </CookiesProvider>
    </ChakraProvider>
  );
}

export default App;